import { useRef, useEffect, useCallback } from "react";
import type { ResonanceFrame } from "@shared/types/resonance";

interface ResonanceMini2DProps {
  frame: ResonanceFrame;
  width?: number;
  height?: number;
  streaming?: boolean;
}

const WARM = [255, 180, 40] as const;
const WARM_HOT = [255, 120, 20] as const;
const COOL = [60, 140, 255] as const;
const COOL_DEEP = [30, 80, 220] as const;
const NEUTRAL = [50, 50, 80] as const;

function lerpColor(a: readonly number[], b: readonly number[], t: number): string {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

function nodeColor(g: -1 | 0 | 1, a: number): { color: string; glow: string; alpha: number } {
  if (g === 1) {
    const color = lerpColor(WARM, WARM_HOT, a);
    const glow = lerpColor(WARM, WARM_HOT, Math.min(a * 1.3, 1));
    return { color, glow, alpha: 0.3 + a * 0.7 };
  }
  if (g === -1) {
    const color = lerpColor(COOL, COOL_DEEP, a);
    const glow = lerpColor(COOL, COOL_DEEP, Math.min(a * 1.3, 1));
    return { color, glow, alpha: 0.3 + a * 0.7 };
  }
  return { color: lerpColor(NEUTRAL, [90, 90, 120], a), glow: "rgb(90,90,120)", alpha: 0.15 + a * 0.5 };
}

export function ResonanceMini2D({ frame, width, height, streaming }: ResonanceMini2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const timeRef = useRef(0);
  const rampRef = useRef(0);
  const lastSeedRef = useRef(frame.seed);

  useEffect(() => {
    if (streaming && frame.seed !== lastSeedRef.current) {
      rampRef.current = 0;
      lastSeedRef.current = frame.seed;
    }
  }, [streaming, frame.seed]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number, t: number) => {
    ctx.clearRect(0, 0, w, h);

    if (streaming) {
      rampRef.current = Math.min(rampRef.current + 0.008, 1);
    } else {
      rampRef.current = 1;
    }
    const ramp = rampRef.current;
    const nodeLimit = streaming ? Math.floor(frame.nodes.length * ramp) : frame.nodes.length;
    const glowMult = streaming ? 0.3 + ramp * 0.7 : 1;
    const settleGlow = frame.frozen ? 0.85 : 1;

    const cx = w / 2;
    const cy = h / 2;
    const scale = Math.min(w, h) * 0.4;
    const pulse = frame.frozen ? 0 : Math.sin(t * 2.0) * 0.06 * glowMult;
    const breathe = frame.frozen ? 1 : 1 + Math.sin(t * 0.8) * 0.03;

    const warmCount = frame.nodes.filter(n => n.g === 1).length;
    const coolCount = frame.nodes.filter(n => n.g === -1).length;
    const total = frame.nodes.length || 1;
    const warmRatio = warmCount / total;
    const coolRatio = coolCount / total;

    const ambientGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);
    if (warmRatio > coolRatio && warmRatio > 0.2) {
      ambientGrad.addColorStop(0, `rgba(255, 160, 40, ${0.08 + warmRatio * 0.12})`);
      ambientGrad.addColorStop(0.5, `rgba(255, 120, 20, ${0.03 + warmRatio * 0.05})`);
      ambientGrad.addColorStop(1, "transparent");
    } else if (coolRatio > 0.2) {
      ambientGrad.addColorStop(0, `rgba(60, 130, 255, ${0.08 + coolRatio * 0.12})`);
      ambientGrad.addColorStop(0.5, `rgba(40, 80, 220, ${0.03 + coolRatio * 0.05})`);
      ambientGrad.addColorStop(1, "transparent");
    } else {
      ambientGrad.addColorStop(0, `rgba(80, 80, 140, 0.06)`);
      ambientGrad.addColorStop(1, "transparent");
    }
    ctx.fillStyle = ambientGrad;
    ctx.fillRect(0, 0, w, h);

    const visibleNodes = frame.nodes.slice(0, nodeLimit);

    const projected = visibleNodes.map(n => ({
      px: cx + (n.x * 0.72 + n.z * 0.18) * scale * breathe,
      py: cy + (n.y * 0.72 - n.z * 0.12) * scale * breathe,
      a: n.a * glowMult * settleGlow,
      g: n.g,
    }));

    if (frame.edges) {
      for (const [s, e] of frame.edges) {
        if (s >= projected.length || e >= projected.length) continue;
        const ps = projected[s];
        const pe = projected[e];
        const strength = Math.min(ps.a, pe.a);
        if (strength < 0.05) continue;
        const edgeG = ps.g === 1 || pe.g === 1 ? 1 : ps.g === -1 || pe.g === -1 ? -1 : 0;
        const { color } = nodeColor(edgeG as -1|0|1, Math.max(ps.a, pe.a));

        const edgeAlpha = strength * 0.6;
        const lineW = 0.5 + strength * 2;

        if (strength > 0.4) {
          ctx.beginPath();
          ctx.moveTo(ps.px, ps.py);
          ctx.lineTo(pe.px, pe.py);
          ctx.strokeStyle = color;
          ctx.globalAlpha = edgeAlpha * 0.3;
          ctx.lineWidth = lineW + 3;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.moveTo(ps.px, ps.py);
        ctx.lineTo(pe.px, pe.py);
        ctx.strokeStyle = color;
        ctx.globalAlpha = edgeAlpha;
        ctx.lineWidth = lineW;
        ctx.stroke();
      }
    }

    ctx.globalAlpha = 1;
    for (const p of projected) {
      const { color, glow, alpha } = nodeColor(p.g, p.a);
      const baseR = 1.5 + p.a * 4;
      const r = baseR * (1 + pulse);

      if (p.a > 0.15) {
        const outerGlow = ctx.createRadialGradient(p.px, p.py, 0, p.px, p.py, r * 5);
        outerGlow.addColorStop(0, glow);
        outerGlow.addColorStop(0.4, color);
        outerGlow.addColorStop(1, "transparent");
        ctx.globalAlpha = alpha * 0.4;
        ctx.fillStyle = outerGlow;
        ctx.fillRect(p.px - r * 5, p.py - r * 5, r * 10, r * 10);
      }

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(p.px, p.py, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      if (p.a > 0.5) {
        ctx.globalAlpha = alpha * 0.8;
        ctx.beginPath();
        ctx.arc(p.px, p.py, r * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
    if (frame.coherence > 0) {
      const barW = w * 0.3;
      const barH = 3;
      const barX = w - barW - 12;
      const barY = h - 14;

      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.beginPath();
      ctx.roundRect(barX, barY, barW, barH, 2);
      ctx.fill();

      const fillColor = frame.coherence > 0.6
        ? lerpColor(COOL, WARM, (frame.coherence - 0.6) / 0.4)
        : lerpColor(NEUTRAL, COOL, frame.coherence / 0.6);
      ctx.fillStyle = fillColor;
      ctx.globalAlpha = 0.7;
      ctx.beginPath();
      ctx.roundRect(barX, barY, barW * frame.coherence, barH, 2);
      ctx.fill();

      if (frame.coherence > 0.3) {
        const barGlow = ctx.createLinearGradient(barX, barY - 4, barX, barY + barH + 4);
        barGlow.addColorStop(0, "transparent");
        barGlow.addColorStop(0.5, fillColor);
        barGlow.addColorStop(1, "transparent");
        ctx.fillStyle = barGlow;
        ctx.globalAlpha = 0.15;
        ctx.fillRect(barX, barY - 4, barW * frame.coherence, barH + 8);
      }

      ctx.globalAlpha = 0.4;
      ctx.font = "9px system-ui, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.textAlign = "right";
      ctx.fillText(`${Math.round(frame.coherence * 100)}%`, barX - 4, barY + barH);
    }

    ctx.globalAlpha = 1;
  }, [frame, streaming]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const parent = canvas.parentElement;
    const w = width || parent?.clientWidth || 300;
    const h = height || parent?.clientHeight || 120;
    const dpr = Math.min(window.devicePixelRatio, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.scale(dpr, dpr);

    if (frame.frozen && !streaming) {
      draw(ctx, w, h, 0);
      return;
    }

    let running = true;
    const animate = () => {
      if (!running) return;
      timeRef.current += 0.016;
      draw(ctx, w, h, timeRef.current);
      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      running = false;
      cancelAnimationFrame(animRef.current);
    };
  }, [frame, width, height, draw]);

  return (
    <canvas
      ref={canvasRef}
      className="block w-full h-full"
      data-testid="canvas-resonance-mini"
    />
  );
}
